import React, { useMemo, useState } from "react";
import {
  amountTxt,
  btc,
  conversionFeeInfoMsg,
  conversionFeeLabel,
  gasFee,
  gasFeeMsg,
  maxAmount,
  minAmount,
  networkFee,
  networkFeeMsg,
  routingFee,
  routingFeeMsg,
  sats,
  speedFee,
  speedFeeMsg,
  totalAmountToSend,
  baseFee,
  baseFeeMsg,
  XAUT,
} from "./messages";
import { useSelector } from "react-redux";
import {
  btcToSatsAmount,
  displayAmountWithCurrency,
  getSpeedFeeLabel,
  showAmount,
  stableCoins,
} from "./constants";
import CommonSkeleton from "./CommonSkeleton";
import QrInfoBox from "./QrInfoBox";

function FeesComponentCommon({
  calculatedSpeedFees,
  isLightningMethod,
  networkFeeAmount,
  conversionFeeAmount,
  amount,
  isFromBrowser,
  checkExchange,
  exchangeRate,
  isLnUrlMethodReceive,
  min_amount,
  max_amount,
  networkFeeLoader,
  isOnchainMethod,
  isEthereumMethod,
  currency,
  isTronMethod,
  isSolanaMethod,
  isTonMethod,
  speedFeesPercentage,
  fixedFee,
}) {
  const { walletCurrency, showBtcAmount } = useSelector(
    (state) => state.common
  );

  const { symbol: targetedCurrencySymbol, code: targetedCurrency } = {
    ...walletCurrency,
  };

  const isShowBtcAmount = showBtcAmount && currency === sats;

  const networkFeeInfo = useMemo(() => {
    let label = "";
    let msg = "";

    if (isOnchainMethod || isTronMethod) {
      label = networkFee;
      msg = networkFeeMsg;
    } else if (isLightningMethod) {
      label = routingFee;
      msg = routingFeeMsg;
    } else if (isEthereumMethod) {
      label = gasFee;
      msg = gasFeeMsg;
    } else if (isSolanaMethod || isTonMethod) {
      label = baseFee;
      msg = baseFeeMsg;
    }

    return { label, msg };
  }, [isLightningMethod, isEthereumMethod]);

  const displayAmount = ({
    amount,
    label = null,
    showPlusSign = true,
    networkFeeLoader = false,
  }) => {
    const showWalletCurrencySymbol = checkExchange && targetedCurrencySymbol;
    const showAssetCurrency =
      !checkExchange && (isShowBtcAmount ? btc : currency);
    const satsAmount = isShowBtcAmount ? btcToSatsAmount(amount) : amount;
    const finalConvertedAmount = satsAmount / exchangeRate;

    const zeroAmt = displayAmountWithCurrency(
      checkExchange ? targetedCurrency : showAssetCurrency,
      0
    );

    return networkFeeLoader ? (
      <CommonSkeleton />
    ) : (
      <>
        {showPlusSign && "+ "}
        {(label === routingFee || label === conversionFeeLabel) && (
          <>{zeroAmt} to </>
        )}
        {showWalletCurrencySymbol}
        {showAmount({
          amount: checkExchange ? finalConvertedAmount : amount,
          currency: checkExchange
            ? targetedCurrency
            : isShowBtcAmount
              ? btc
              : currency,
          showCommaSeparated: true,
          appendCurrency: !checkExchange && currency,
          multiplier:
            stableCoins.includes(currency) ||
            checkExchange ||
            currency === XAUT,
        })}
      </>
    );
  };

  let sendInfoArr = [
    {
      label: amountTxt,
      value: displayAmount({ amount, showPlusSign: false }),
    },
    {
      label: getSpeedFeeLabel({
        speedFeePercentage: speedFeesPercentage,
        speedFixedFees: fixedFee,
      }),
      value: displayAmount({ amount: calculatedSpeedFees }),
      showInfo: true,
      infoMsg: speedFeeMsg,
    },
    {
      label: networkFeeInfo?.label,
      value: displayAmount({
        amount: networkFeeAmount,
        label: networkFeeInfo?.label,
        networkFeeLoader,
      }),
      showInfo: true,
      infoMsg: networkFeeInfo?.msg,
    },
  ];

  !isLightningMethod &&
    sendInfoArr.push({
      label: totalAmountToSend,
      value: displayAmount({
        amount: (
          Number(amount) +
          networkFeeAmount +
          calculatedSpeedFees
        ).toFixed(currency === sats ? 8 : currency === XAUT ? 6 : 2),
        networkFeeLoader,
        showPlusSign: false,
      }),
    });

  isLightningMethod &&
    sendInfoArr.push({
      label: conversionFeeLabel,
      value: displayAmount({
        amount: conversionFeeAmount,
        label: conversionFeeLabel,
      }),
      showInfo: true,
      infoMsg: conversionFeeInfoMsg,
    });

  const lnUrlReceiveInfoArr = [
    ...(min_amount === max_amount
      ? [{ label: amountTxt, value: displayAmount({ amount: min_amount }) }]
      : [
          { label: minAmount, value: displayAmount({ amount: min_amount }) },
          { label: maxAmount, value: displayAmount({ amount: max_amount }) },
        ]),
    {
      label: getSpeedFeeLabel({
        speedFeePercentage: speedFeesPercentage,
        speedFixedFees: fixedFee,
      }),
      value: displayAmount({ amount: calculatedSpeedFees }),
      showInfo: true,
      infoMsg: speedFeeMsg,
    },
  ];

  return (
    <QrInfoBox
      {...{
        infoData: isLnUrlMethodReceive ? lnUrlReceiveInfoArr : sendInfoArr,
        isFromBrowser,
        isFrom: "send",
        currency,
        networkFeeLoader,
      }}
    />
  );
}

export default React.memo(FeesComponentCommon);
